<header class="sticky top-0 z-40 border-b border-cp-line bg-cp-bg/75 backdrop-blur-xl backdrop-saturate-150">
  <nav aria-label="Main navigation" class="relative mx-auto flex h-16 max-w-[1200px] items-center gap-6 px-5 sm:px-8">
    <!-- Logo and wordmark: unchanged placeholder until the new mark lands. -->
    <a routerLink="/" (click)="onLogoClick($event)" aria-label="creditpath.io home" class="flex shrink-0 items-center gap-2 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cp-accent">
      <img src="assets/img/creditpathlogo.webp" alt="" width="24" height="24" class="h-6 w-6" />
      <span class="text-[16px] font-[560] lowercase tracking-[-0.02em] text-cp-ink">creditpath.io</span>
    </a>

    <!-- Desktop: links pushed right, hairline divider, account area. -->
    <div class="ml-auto hidden items-center lg:flex">
      <ul class="flex items-center gap-0.5">
        <li (pointerenter)="onSolutionsPointerEnter()" (pointerleave)="onSolutionsPointerLeave()">
          <button
            #solutionsTrigger
            type="button"
            (click)="toggleSolutions()"
            (keydown)="onSolutionsTriggerKeydown($event)"
            (focusout)="onSolutionsFocusOut($event)"
            [attr.aria-expanded]="solutionsOpen()"
            aria-controls="solutions-menu"
            [class]="navLink + ' inline-flex items-center gap-1 ' + (solutionsOpen() ? '!text-cp-ink bg-cp-fill' : '')">
            Credit Solutions
            <ng-icon name="phosphorCaretDown" size="12" [class]="'transition-transform duration-150 ' + (solutionsOpen() ? 'rotate-180' : '')" aria-hidden="true" />
          </button>
        </li>
        <li><button type="button" (click)="goToSection('pricing')" [class]="navLink">Pricing</button></li>
        <li><a routerLink="/about" routerLinkActive="!text-cp-ink" [class]="navLink">About</a></li>
        <li><a routerLink="/contact" routerLinkActive="!text-cp-ink" [class]="navLink">Contact</a></li>
      </ul>

      <button
        type="button"
        (click)="toggleTheme()"
        class="ml-2 grid h-8 w-8 place-items-center rounded-full text-cp-muted transition-colors hover:bg-cp-fill hover:text-cp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent"
        [attr.aria-label]="isDarkMode() ? 'Switch to light mode' : 'Switch to dark mode'">
        <ng-icon [name]="isDarkMode() ? 'phosphorSun' : 'phosphorMoon'" size="16" aria-hidden="true" />
      </button>

      <span class="mx-4 h-4 w-px bg-cp-line-strong" aria-hidden="true"></span>

      @if (!isAuthenticated()) {
        <div class="flex items-center gap-2">
          <button type="button" (click)="handleLogin()" [class]="navLink">Log In</button>
          <button type="button" (click)="signUp()" [class]="btnPrimarySm">Sign Up</button>
        </div>
      } @else {
        <div class="flex items-center gap-2">
          @if (portal; as p) {
            <a [routerLink]="p.path" [class]="btnPrimarySm">{{ p.label }}</a>
          }
          <div class="relative">
            <button
              #accountTrigger
              type="button"
              (click)="toggleUserMenu()"
              [attr.aria-expanded]="userMenuOpen()"
              aria-controls="account-menu"
              aria-label="Account menu"
              class="flex items-center gap-1 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-cp-fill focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent">
              <span class="grid h-7 w-7 place-items-center rounded-full bg-cp-fill text-[12px] font-[510] text-cp-ink">{{ userInitial() }}</span>
              <ng-icon name="phosphorCaretDown" size="12" class="text-cp-subtle" aria-hidden="true" />
            </button>
            @if (userMenuOpen()) {
              <div id="account-menu" class="cp-pop absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-cp-line-strong bg-cp-surface py-1.5 shadow-cp">
                <div class="border-b border-cp-line px-4 py-3">
                  <p class="text-sm font-[510] text-cp-ink">{{ userName() || 'User' }}</p>
                  <p class="truncate text-xs text-cp-subtle">{{ userEmail() }}</p>
                </div>
                <div class="py-1">
                  @if (showAdvisorPortal) {
                    <button type="button" (click)="navigateToAdvisorPortal()" [class]="menuItem">
                      <ng-icon name="phosphorSquaresFour" size="16" class="text-cp-subtle" aria-hidden="true" />Advisor Portal
                    </button>
                  }
                  @if (showLenderPortal) {
                    <button type="button" (click)="navigateToLenderPortal()" [class]="menuItem">
                      <ng-icon name="phosphorBank" size="16" class="text-cp-subtle" aria-hidden="true" />Lender Portal
                    </button>
                  }
                  <button type="button" (click)="navigateToSettings()" [class]="menuItem">
                    <ng-icon name="phosphorGear" size="16" class="text-cp-subtle" aria-hidden="true" />Settings
                  </button>
                </div>
                <div class="border-t border-cp-line pt-1">
                  <button type="button" (click)="signOut()" [class]="menuItem + ' !text-cp-danger'">
                    <ng-icon name="phosphorSignOut" size="16" aria-hidden="true" />Sign out
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Mobile controls -->
    <div class="ml-auto flex items-center gap-1 lg:hidden">
      <button
        type="button"
        (click)="toggleTheme()"
        class="grid h-9 w-9 place-items-center rounded-full text-cp-muted transition-colors hover:bg-cp-fill hover:text-cp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent"
        [attr.aria-label]="isDarkMode() ? 'Switch to light mode' : 'Switch to dark mode'">
        <ng-icon [name]="isDarkMode() ? 'phosphorSun' : 'phosphorMoon'" size="16" aria-hidden="true" />
      </button>
      <button
        type="button"
        (click)="toggleMobileMenu()"
        [attr.aria-label]="mobileMenuOpen ? 'Close menu' : 'Open menu'"
        [attr.aria-expanded]="mobileMenuOpen"
        [attr.aria-controls]="mobileMenuOpen ? 'mobile-menu' : null"
        class="grid h-9 w-9 place-items-center rounded-full text-cp-ink transition-colors hover:bg-cp-fill focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent">
        <ng-icon [name]="mobileMenuOpen ? 'phosphorX' : 'phosphorList'" size="20" aria-hidden="true" />
      </button>
    </div>

    <!-- Credit Solutions menu (desktop) -->
    @if (solutionsOpen()) {
      <div
        #solutionsPanel
        id="solutions-menu"
        (pointerenter)="onSolutionsPointerEnter()"
        (pointerleave)="onSolutionsPointerLeave()"
        (keydown)="onSolutionsPanelKeydown($event)"
        (focusout)="onSolutionsFocusOut($event)"
        class="cp-pop absolute right-5 top-[calc(100%+8px)] hidden w-[min(880px,calc(100%-2.5rem))] overflow-hidden rounded-xl border border-cp-line-strong bg-cp-surface shadow-cp sm:right-8 lg:block">
        <div class="grid grid-cols-3 divide-x divide-cp-line p-2">
          @for (group of solutionGroups; track group.title) {
            <div class="px-2 py-2">
              <p class="px-3 pb-2 text-[12px] text-cp-subtle">{{ group.title }}</p>
              <ul>
                @for (item of group.items; track item.id) {
                  <li>
                    <a
                      data-menu-item
                      [attr.href]="solutionHref(item.id)"
                      (click)="selectSolution($event, item.id)"
                      class="block rounded-lg px-3 py-2.5 transition-colors hover:bg-cp-fill focus-visible:bg-cp-fill focus-visible:outline-none">
                      <span class="block text-[14px] text-cp-ink">{{ item.title }}</span>
                      <span class="mt-0.5 block text-[13px] leading-snug text-cp-muted">{{ item.description }}</span>
                    </a>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
        <div class="flex items-center justify-between border-t border-cp-line px-6 py-3.5 text-[13px]">
          <a data-menu-item [attr.href]="solutionHref('overview')" (click)="selectSolution($event, 'overview')" class="inline-flex items-center gap-1 rounded-sm text-cp-ink hover:text-cp-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent">
            All credit solutions <ng-icon name="phosphorArrowRight" size="13" aria-hidden="true" />
          </a>
          <a data-menu-item href="/documents/white-paper" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 rounded-sm text-cp-muted hover:text-cp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent">
            View White Paper <ng-icon name="phosphorArrowUpRight" size="13" aria-hidden="true" />
            <span class="sr-only">(opens in a new tab)</span>
          </a>
        </div>
      </div>
    }
  </nav>

  <!-- Mobile menu -->
  @if (mobileMenuOpen) {
    <div id="mobile-menu" class="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-cp-line px-5 pb-6 pt-2 sm:px-8 lg:hidden">
      <ul class="flex flex-col">
        <li>
          <button
            type="button"
            (click)="mobileSolutionsOpen.set(!mobileSolutionsOpen())"
            [attr.aria-expanded]="mobileSolutionsOpen()"
            aria-controls="mobile-solutions"
            class="flex w-full items-center justify-between py-3 text-left text-base text-cp-ink">
            Credit Solutions
            <ng-icon name="phosphorCaretDown" size="14" [class]="'text-cp-subtle transition-transform ' + (mobileSolutionsOpen() ? 'rotate-180' : '')" aria-hidden="true" />
          </button>
          @if (mobileSolutionsOpen()) {
            <div id="mobile-solutions" class="space-y-4 pb-3 pl-3">
              @for (group of solutionGroups; track group.title) {
                <div>
                  <p class="text-[12px] text-cp-subtle">{{ group.title }}</p>
                  <ul class="mt-1">
                    @for (item of group.items; track item.id) {
                      <li>
                        <a [attr.href]="solutionHref(item.id)" (click)="selectSolution($event, item.id)" class="block py-2 text-[15px] text-cp-muted hover:text-cp-ink">{{ item.title }}</a>
                      </li>
                    }
                  </ul>
                </div>
              }
            </div>
          }
        </li>
        <li><button type="button" (click)="goToSection('pricing')" class="w-full py-3 text-left text-base text-cp-ink">Pricing</button></li>
        <li><a routerLink="/about" (click)="mobileMenuOpen = false" class="block py-3 text-base text-cp-ink">About</a></li>
        <li><a routerLink="/contact" (click)="mobileMenuOpen = false" class="block py-3 text-base text-cp-ink">Contact</a></li>
      </ul>
      <div class="mt-3 border-t border-cp-line pt-5">
        @if (!isAuthenticated()) {
          <div class="grid grid-cols-2 gap-3">
            <button type="button" (click)="handleLogin()" [class]="btnSecondary + ' w-full'">Log In</button>
            <button type="button" (click)="signUp()" [class]="btnPrimary + ' w-full'">Sign Up</button>
          </div>
        } @else {
          <p class="text-sm font-[510] text-cp-ink">{{ userName() || 'User' }}</p>
          <p class="truncate text-xs text-cp-subtle">{{ userEmail() }}</p>
          <div class="mt-3 flex flex-col">
            @if (showAdvisorPortal) {
              <a routerLink="/advisor-portal" (click)="mobileMenuOpen = false" class="py-2.5 text-base text-cp-ink">Advisor Portal</a>
            }
            @if (showLenderPortal) {
              <a routerLink="/lender-portal" (click)="mobileMenuOpen = false" class="py-2.5 text-base text-cp-ink">Lender Portal</a>
            }
            <button type="button" (click)="navigateToSettings()" class="py-2.5 text-left text-base text-cp-ink">Settings</button>
            <button type="button" (click)="signOut()" class="py-2.5 text-left text-base text-cp-danger">Sign out</button>
          </div>
        }
      </div>
    </div>
  }
</header>
